import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import { BudgetRequestAnalyticsTypeEnum } from '@zaoblako/shared/modules/task/enum/budget-request-analytics-type.enum';
import { BudgetRequestAnalyticsTypeEnumI18n } from '@zaoblako/shared/modules/task/i18n/budget-request-analytics-type-enum.i18n';
import { BudgetRequestItemFormDto, type BudgetRequestItemFormInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/item/budget-request-item-form.dto';
import { ListStyleEnum } from '@zaoblako/shared/common/enums/list-style.enum';
import { ListStyleEnumI18n } from '@zaoblako/shared/common/i18n/list-style-enum.i18n';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { BudgetRequestTypeEnum } from '@zaoblako/shared/modules/task/enum/budget-request-type.enum';
import { BudgetRequestTypeEnumI18n } from '@zaoblako/shared/modules/task/i18n/budget-request-type-enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';

import * as yup from 'yup';

/** Detailed request for budget allocation for a specific month (create and update requests body) */
export interface BudgetRequestFormInterface {
  type?: BudgetRequestTypeEnum;
  /** Budget request analytics type. Using only when {@link BudgetRequestFormInterface.type} equals {@link BudgetRequestTypeEnum.PLAN}*/
  analyticsType?: BudgetRequestAnalyticsTypeEnum;
  /** Budget request display name */
  name: string;
  /** Budget request period (month and year) */
  period: Date;
  transferDate?: Date;
  /** Approval scheme to use */
  scheme?: ApprovalSchemeFindInterface;
  /** Company to plan budget */
  company?: CompanyFindInterface;
  /** Unit analytical feature */
  unit?: UnitFindInterface;
  /** Filial analytical feature */
  filial?: FilialFindInterface;
  /** Cost center */
  costCenter?: CostCenterFindInterface;
  /** Deals group to plan budget */
  dealsGroup?: DealsGroupFindInterface;
  itemsFormat?: ListStyleEnum;
  /** Budget amount values per directions */
  items: BudgetRequestItemFormInterface[];
  /** User auditors - other budget request participants */
  userAuditors?: UserFindInterface[];
  /** User groups auditors - other budget request participants */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Related projects (to update this list in an existing budget request, you must use a separate API) */
  projects?: ProjectFindInterface[];
}

/** Detailed request for budget allocation for a specific month (create and update requests body) - DTO schema */
export const BudgetRequestFormDto = SerdeDtoClass.create<BudgetRequestFormInterface>(TaskDtoKeysEnum.BUDGET_REQUEST_FORM_DTO, {
  type: {
    optional: true,
    label: 'Тип',
    serializer: commonEnumValidator(BudgetRequestTypeEnum, BudgetRequestTypeEnumI18n),
  },
  analyticsType: {
    optional: true,
    label: 'Тип аналитики',
    serializer: commonEnumValidator(BudgetRequestAnalyticsTypeEnum, BudgetRequestAnalyticsTypeEnumI18n),
  },
  name: {
    label: 'Имя',
    serializer: yup.string().required(DtoMessageEnum.REQUIRED_FIELD),
  },
  period: {
    label: 'Период',
    serializer: yup.date(),
  },
  transferDate: {
    optional: true,
    label: 'Дата переноса',
    serializer: yup.date(),
  },
  scheme: {
    optional: true,
    label: 'Схема',
    serializer: entityLikeSchema,
  },
  company: {
    optional: true,
    label: 'Компания',
    serializer: entityLikeSchema,
  },
  unit: {
    optional: true,
    label: 'БЕ',
    serializer: entityLikeSchema,
  },
  filial: {
    optional: true,
    label: 'Филиал',
    serializer: entityLikeSchema,
  },
  costCenter: {
    optional: true,
    label: 'Центр затрат',
    serializer: entityLikeSchema,
  },
  dealsGroup: {
    optional: true,
    label: 'Группа сделки',
    serializer: entityLikeSchema,
  },
  itemsFormat: {
    optional: true,
    label: 'Формат отображения',
    serializer: commonEnumValidator(ListStyleEnum, ListStyleEnumI18n),
  },
  items: {
    label: 'Заявки на бюджет',
    nested: BudgetRequestItemFormDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userAuditors: {
    optional: true,
    label: 'Аудиторы',
    serializer: yup.array(entityLikeSchema),
  },
  userGroupAuditors: {
    optional: true,
    label: 'Группы аудиторов',
    serializer: yup.array(entityLikeSchema),
  },
  projects: {
    optional: true,
    label: 'Проекты',
    serializer: yup.array(entityLikeSchema),
  },
});
